/**
 * Helper para capturar datos de analytics del usuario
 * Incluye: UTM params, referrer, device info, geo location, etc.
 */

import {
  BRAND_POSTHOG_PROJECT_ID,
  BRAND_POSTHOG_UI_HOST,
} from "@/lib/posthogBrandAlignment";

export interface ConversionAnalytics {
  // UTM Parameters
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;

  // Referrer & Landing
  referrer?: string;
  landing_page?: string;
  original_referrer?: string;

  // Device Info
  device?: "mobile" | "desktop" | "tablet";
  browser?: string;
  os?: string;
  screen_resolution?: string;

  // Geo (si está disponible en el contexto)
  country?: string;
  city?: string;
  region?: string;

  // User Behavior
  session_count?: number;
  first_visit?: string;
  is_returning?: boolean;
  time_on_site?: number;

  // PostHog
  distinct_id?: string;
  /** PostHog session id (UUID) — correlates with replay + MCP */
  posthog_session_id?: string;
  /** PostHog session replay deep link when recording is on */
  posthog_session_replay_url?: string;
}

/**
 * Detecta el tipo de dispositivo basado en user agent
 */
function detectDevice(): "mobile" | "desktop" | "tablet" {
  const userAgent = navigator.userAgent.toLowerCase();

  if (
    /ipad|android(?!.*mobile)|tablet/.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  ) {
    return "tablet";
  }

  if (
    /android|webos|iphone|ipod|blackberry|iemobile|opera mini/.test(
      userAgent,
    )
  ) {
    return "mobile";
  }

  return "desktop";
}

/**
 * Detecta el navegador
 */
function detectBrowser(): string {
  const userAgent = navigator.userAgent;

  if (userAgent.includes("Chrome")) return "Chrome";
  if (userAgent.includes("Safari")) return "Safari";
  if (userAgent.includes("Firefox")) return "Firefox";
  if (userAgent.includes("Edge")) return "Edge";
  if (userAgent.includes("Opera")) return "Opera";

  return "Unknown";
}

/**
 * Detecta el sistema operativo
 */
function detectOS(): string {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;

  if (/iPhone|iPad|iPod/.test(userAgent)) return "iOS";
  if (/Android/.test(userAgent)) return "Android";
  if (/Win/.test(platform)) return "Windows";
  if (/Mac/.test(platform)) return "macOS";
  if (/Linux/.test(platform)) return "Linux";

  return "Unknown";
}

/**
 * Obtiene UTM parameters de la URL actual o del sessionStorage
 */
function getUtmParams(): Partial<ConversionAnalytics> {
  const params = new URLSearchParams(window.location.search);

  // Primero buscar en URL actual
  const utmSource = params.get("utm_source");
  const utmMedium = params.get("utm_medium");
  const utmCampaign = params.get("utm_campaign");
  const utmContent = params.get("utm_content");
  const utmTerm = params.get("utm_term");

  // Si no están en URL, buscar en sessionStorage (guardados desde landing)
  if (!utmSource) {
    return {
      utm_source: sessionStorage.getItem("utm_source") || undefined,
      utm_medium: sessionStorage.getItem("utm_medium") || undefined,
      utm_campaign: sessionStorage.getItem("utm_campaign") || undefined,
      utm_content: sessionStorage.getItem("utm_content") || undefined,
      utm_term: sessionStorage.getItem("utm_term") || undefined,
    };
  }

  return {
    utm_source: utmSource || undefined,
    utm_medium: utmMedium || undefined,
    utm_campaign: utmCampaign || undefined,
    utm_content: utmContent || undefined,
    utm_term: utmTerm || undefined,
  };
}

/**
 * Guarda UTM parameters en sessionStorage cuando el usuario llega al sitio
 * Llamar esto en la página de landing/bio
 */
export function captureUtmParams(): void {
  const params = new URLSearchParams(window.location.search);

  const utmParams = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
  ];

  utmParams.forEach((param) => {
    const value = params.get(param);
    if (value) {
      sessionStorage.setItem(param, value);
    }
  });

  // También guardar landing page
  if (!sessionStorage.getItem("landing_page")) {
    sessionStorage.setItem("landing_page", window.location.pathname);
  }

  // Guardar referrer si es de fuera del sitio
  const referrer = document.referrer;
  if (referrer && !referrer.includes(window.location.host)) {
    sessionStorage.setItem("original_referrer", referrer);
  }
}

function getPostHogSessionMeta(): {
  session_id?: string;
  session_replay_url?: string;
} {
  try {
    const posthog = (
      window as unknown as {
        posthog?: {
          get_session_id?: () => string;
          get_session_replay_url?: () => string | null;
        };
      }
    ).posthog;
    const sid = posthog?.get_session_id?.();
    const rawReplay = posthog?.get_session_replay_url?.();
    // Prefer UI host + numeric project id — never first-party /ingest + phc_ token paths.
    let replay: string | undefined =
      typeof rawReplay === "string" && rawReplay.trim() ? rawReplay.trim() : undefined;
    const sidOk = typeof sid === "string" && sid.trim().length >= 8;
    const replayOk =
      !!replay &&
      replay.startsWith("https://") &&
      !replay.includes("/ingest") &&
      !replay.includes("/project/phc_") &&
      replay.includes(`/project/${BRAND_POSTHOG_PROJECT_ID}/replay/`);
    if (!replayOk && sidOk) {
      const uiHostRaw = String(
        import.meta.env.VITE_POSTHOG_UI_HOST || BRAND_POSTHOG_UI_HOST,
      )
        .trim()
        .replace(/\/+$/, "");
      const uiHost =
        !uiHostRaw ||
        uiHostRaw.startsWith("/") ||
        uiHostRaw.includes("i.posthog") ||
        uiHostRaw.includes("/ingest")
          ? BRAND_POSTHOG_UI_HOST
          : uiHostRaw;
      // Locked: entire brand → PostHog project 422815
      replay = `${uiHost}/project/${BRAND_POSTHOG_PROJECT_ID}/replay/${encodeURIComponent(sid!.trim())}`;
    }
    return {
      session_id: sidOk ? sid!.trim() : undefined,
      session_replay_url: replayOk || sidOk ? replay : undefined,
    };
  } catch {
    return {};
  }
}

function getPostHogDistinctId(): string | undefined {
  try {
    // @ts-expect-error - PostHog might not be loaded
    const posthog = window.posthog;
    if (posthog && posthog.get_distinct_id) {
      return posthog.get_distinct_id();
    }
  } catch {
    // PostHog no está disponible
  }
  return undefined;
}

/**
 * Captura toda la información de analytics del usuario
 */
export function captureConversionAnalytics(): ConversionAnalytics {
  const device = detectDevice();
  const browser = detectBrowser();
  const os = detectOS();
  const utmParams = getUtmParams();

  // Obtener referrer
  const referrer =
    document.referrer || sessionStorage.getItem("original_referrer") || "";

  // Obtener landing page
  const landingPage =
    sessionStorage.getItem("landing_page") || window.location.pathname;

  // Calcular tiempo en el sitio
  const landingTime = parseInt(
    sessionStorage.getItem("landing_time") || "0",
    10,
  );
  const timeOnSite = landingTime
    ? Math.floor((Date.now() - landingTime) / 1000)
    : 0;

  // Verificar si es usuario recurrente
  const firstVisit = sessionStorage.getItem("first_visit");
  const isReturning = !!firstVisit;

  const phSession = getPostHogSessionMeta();

  return {
    ...utmParams,
    referrer,
    landing_page: landingPage,
    original_referrer: sessionStorage.getItem("original_referrer") || undefined,
    device,
    browser,
    os,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    distinct_id: getPostHogDistinctId(),
    posthog_session_id: phSession.session_id,
    posthog_session_replay_url: phSession.session_replay_url,
    first_visit: firstVisit || undefined,
    is_returning: isReturning,
    time_on_site: timeOnSite,
  };
}

/**
 * Inicializa el tracking cuando el usuario llega al sitio
 * Llamar esto en la página principal/bio
 */
export function initConversionTracking(): void {
  // Capturar UTMs
  captureUtmParams();

  // Guardar tiempo de llegada
  if (!sessionStorage.getItem("landing_time")) {
    sessionStorage.setItem("landing_time", Date.now().toString());
  }

  // Guardar primera visita si no existe
  if (!sessionStorage.getItem("first_visit")) {
    sessionStorage.setItem("first_visit", new Date().toISOString());
  }
}

/**
 * Limpia los datos de analytics (llamar después de conversión exitosa)
 */
export function clearConversionTracking(): void {
  // Mantener algunos datos para referencia futura
  // Solo limpiar los UTMs que ya no son necesarios
  const keysToRemove = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
  ];

  keysToRemove.forEach((key) => {
    sessionStorage.removeItem(key);
  });
}
